/** Sigils (N): sigil points and activation, Rune Stone etching, and gem sockets. */
import { useState } from 'preact/hooks';
import { GEM_MAX_LEVEL, MAX_ACTIVE_SIGILS, SIGILS, STONE_NODES } from '../../game/build';
import { equip } from '../../game/inventory';
import { ITEMS, type StoneItem, stonePoints } from '../../game/items';
import { registry } from '../../sim/registry';
import { skillIcon } from '../../ui/icons';
import { ui } from '../../ui/app/store';
import { Window } from '../../ui/app/Window';
import { gemIcon, stoneIcon } from './art';
import { type Ctx, ItemCell, Money, commit, notify, useCtx } from './common';
import { keyTrap } from './keys';
import { ETCH_COST, type SigilRow, chanceColor, fmtPct, fuseCost, gemStacks, sigilRows, toggleSigil } from './logic';
import { sigilIcon } from './sigilArt';
import { ItemTooltip, SkillTooltip, tip } from './Tooltip';

type Tab = 'sigils' | 'stone' | 'gems';

// ====================================================================== sigils

function PointsBar({ r }: { r: SigilRow }) {
  const cells: string[] = [];
  const push = (n: number, cls: string) => {
    for (let i = 0; i < n && cells.length < 15; i++) cells.push(cls);
  };
  push(r.books, 'b');
  push(r.acc, 'a');
  push(r.stone, 's');
  while (cells.length < 15) cells.push('');
  return (
    <span class={`bsg-bar ${r.def.negative ? 'is-neg' : ''}`} aria-hidden="true">
      {[0, 1, 2].map((g) => (
        <span key={g} class={`bsg-bar-g ${r.level > g ? 'is-full' : ''}`}>
          {cells.slice(g * 5, g * 5 + 5).map((c, i) => (
            <i key={i} class={c} />
          ))}
        </span>
      ))}
    </span>
  );
}

function SigilRowView({ r, selected, onSelect, onToggle, full }: { r: SigilRow; selected: boolean; onSelect: () => void; onToggle: () => void; full: boolean }) {
  const neg = !!r.def.negative;
  const canTick = r.chosen || (r.level > 0 && !full);
  return (
    <div class={`bsg-row ${selected ? 'is-sel' : ''} ${r.level > 0 ? '' : 'is-dim'} ${neg ? 'is-neg' : ''}`} role="option" aria-selected={selected} tabIndex={selected ? 0 : -1} onClick={onSelect}>
      <span class="bsg-orb">
        <img src={sigilIcon(r.id)} alt="" />
      </span>
      <span class="bsg-name">
        {r.def.name}
        <small>{r.level > 0 ? `Lv. ${r.level}` : neg ? 'Inactive' : `${5 - r.pts} pt${5 - r.pts === 1 ? '' : 's'} to Lv. 1`}</small>
      </span>
      <PointsBar r={r} />
      <span class="bsg-pts">{r.pts}</span>
      {neg ? (
        <span class={`bsg-auto ${r.level > 0 ? 'is-on' : ''}`}>{r.level > 0 ? 'Active' : '—'}</span>
      ) : (
        <label class="bsg-tick" onClick={(e) => e.stopPropagation()} {...tip(() => <div class="btt-note">{r.level > 0 ? 'Activate up to five sigils.' : 'Reach 5 points to activate this sigil.'}</div>)}>
          <input type="checkbox" checked={r.chosen} disabled={!canTick} onChange={onToggle} aria-label={`Activate ${r.def.name}`} />
          <span />
        </label>
      )}
    </div>
  );
}

function Constellation({ ctx, rows }: { ctx: Ctx; rows: SigilRow[] }) {
  const chosen = ctx.c.activeSigils.slice(0, MAX_ACTIVE_SIGILS);
  const cx = 160;
  const cy = 128;
  const R = 92;
  const pts = Array.from({ length: MAX_ACTIVE_SIGILS }, (_, i) => {
    const ang = -Math.PI / 2 + (i * Math.PI * 2) / MAX_ACTIVE_SIGILS;
    return { x: cx + Math.cos(ang) * R, y: cy + Math.sin(ang) * R };
  });
  const rowOf = (id: string | undefined) => rows.find((r) => r.id === id);
  const stone = ctx.c.equipment.stone;
  const live = pts.map((_, i) => {
    const r = rowOf(chosen[i]);
    return !!r && r.level > 0;
  });
  return (
    <div class="bsg-const">
      <svg viewBox="0 0 320 250" class="bsg-const-svg" aria-hidden="true">
        <defs>
          <filter id="bsgGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="#3a3d42" stroke-dasharray="2 5" />
        {pts.map((p, i) => {
          const q = pts[(i + 1) % pts.length]!;
          const on = live[i] && live[(i + 1) % pts.length];
          return on ? <line key={`e${i}`} x1={p.x} y1={p.y} x2={q.x} y2={q.y} class="bsg-link" /> : null;
        })}
        {pts.map((p, i) =>
          live[i] ? (
            <g key={`s${i}`}>
              <line x1={cx} y1={cy} x2={p.x} y2={p.y} class="bsg-spoke-glow" filter="url(#bsgGlow)" />
              <line x1={cx} y1={cy} x2={p.x} y2={p.y} class="bsg-spoke" />
            </g>
          ) : null,
        )}
      </svg>
      <div class="bsg-core" style={{ left: `${(cx / 320) * 100}%`, top: `${(cy / 250) * 100}%` }} {...(stone ? tip(() => <ItemTooltip item={stone} where="equipped" />) : {})}>
        {stone ? <img src={stoneIcon(stone)} alt="" /> : <span>No stone</span>}
      </div>
      {pts.map((p, i) => {
        const r = rowOf(chosen[i]);
        return (
          <div key={i} class={`bsg-node ${r ? (r.level > 0 ? `lv${r.level}` : 'is-off') : 'is-empty'}`} style={{ left: `${(p.x / 320) * 100}%`, top: `${(p.y / 250) * 100}%` }}>
            {r ? <img src={sigilIcon(r.id)} alt="" /> : null}
            {r && (
              <span class="bsg-node-l">
                {r.def.name}
                {r.level > 0 ? ` ${r.level}` : ''}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SigilDetail({ ctx, r }: { ctx: Ctx; r: SigilRow }) {
  const book = `book_${r.id}`;
  const books = ctx.c.inventory.stacks[book] ?? 0;
  const read = () => {
    if (ctx.p.readSigilBook(book)) {
      notify(`Sigil learned: ${r.def.name} ${ctx.c.sigilBooks[r.id] ?? 0}/20`, '#c9a2ff');
      commit();
    } else notify(r.books >= 20 ? 'You have already mastered this codex.' : 'You have no codex for this sigil.', '#e5a24d');
  };
  return (
    <div class={`bsg-detail ${r.def.negative ? 'is-neg' : ''}`}>
      <div class="bsg-detail-h">
        <img src={sigilIcon(r.id)} alt="" />
        <div>
          <div class="bsg-detail-name">{r.def.name}</div>
          <div class="bsg-detail-sub">
            {r.pts} points · {r.level > 0 ? `Level ${r.level}` : 'inactive'}
          </div>
        </div>
      </div>
      <div class="bsg-sources">
        <span class="b">
          <i />
          Codices <b>{r.books}</b>/20
        </span>
        <span class="a">
          <i />
          Accessories <b>{r.acc}</b>
        </span>
        <span class="s">
          <i />
          Rune Stone <b>{r.stone}</b>
        </span>
      </div>
      <ol class="bsg-levels">
        {r.def.levels.map((t, i) => (
          <li key={i} class={r.level === i + 1 ? 'is-cur' : r.level > i ? 'is-past' : ''}>
            <span>
              Lv.{i + 1} <small>{(i + 1) * 5} pts</small>
            </span>
            {t}
          </li>
        ))}
      </ol>
      {!r.def.negative && (
        <div class="bsg-detail-act">
          <button type="button" class="btn" disabled={!books || r.books >= 20} onClick={read}>
            Read Codex{books ? ` (${books})` : ''}
          </button>
        </div>
      )}
    </div>
  );
}

function SigilsTab({ ctx }: { ctx: Ctx }) {
  const rows = sigilRows(ctx.c);
  const [selId, setSelId] = useState<string | null>(null);
  const sel = rows.find((r) => r.id === selId) ?? rows[0];
  const chosen = ctx.c.activeSigils;
  const liveCount = chosen.filter((id) => (rows.find((r) => r.id === id)?.level ?? 0) > 0).length;
  const full = chosen.length >= MAX_ACTIVE_SIGILS;
  const pos = rows.filter((r) => !r.def.negative);
  const neg = rows.filter((r) => r.def.negative);
  const toggle = (id: string) => {
    const next = toggleSigil(ctx.c.activeSigils, id, MAX_ACTIVE_SIGILS);
    if (!next) {
      notify(`You can activate up to ${MAX_ACTIVE_SIGILS} sigils.`, '#e5a24d');
      return;
    }
    ctx.p.setActiveSigils(next);
    commit();
  };
  const row = (r: SigilRow) => <SigilRowView key={r.id} r={r} selected={r.id === sel?.id} full={full} onSelect={() => setSelId(r.id)} onToggle={() => toggle(r.id)} />;
  return (
    <div class="bsg-sigils">
      <div class="bsg-list" role="listbox" aria-label="Sigils">
        <div class="bsg-list-h">
          <span class="section-h">Sigils</span>
          <span class="bsg-count">
            <b>{liveCount}</b> / {MAX_ACTIVE_SIGILS} active
          </span>
          <span class="bsg-legend">
            <i class="b" />
            Codex <i class="a" />
            Accessory <i class="s" />
            Stone
          </span>
        </div>
        {pos.map(row)}
        <div class="bsg-list-h bsg-list-h--neg">
          <span class="section-h">Penalties</span>
          <span class="bsg-count">Active automatically at 5 points</span>
        </div>
        {neg.map(row)}
      </div>
      <div class="bsg-side">
        <Constellation ctx={ctx} rows={rows} />
        {sel && <SigilDetail ctx={ctx} r={sel} />}
      </div>
    </div>
  );
}

// ====================================================================== rune stone

function StoneTab({ ctx }: { ctx: Ctx }) {
  const { c, p, a } = ctx;
  const stones = [c.equipment.stone, ...c.inventory.items.filter((i): i is StoneItem => i.kind === 'stone')].filter((s): s is StoneItem => !!s);
  const [uid, setUid] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ line: number; idx: number; ok: boolean; n: number } | null>(null);
  const [log, setLog] = useState<{ n: number; text: string; ok: boolean; neg: boolean }[]>([]);
  const stone = stones.find((s) => s.uid === uid) ?? stones[0];
  if (!stone) {
    return (
      <div class="bst-empty">
        <img src={stoneIcon({ uid: '', kind: 'stone', name: '', rarity: 'epic', chance: 0.75, lines: [], bound: true })} alt="" />
        <p>You have no Rune Stone yet.</p>
        <p class="muted">Rune Stones are found in Rift Delves and Colossus Hunts. Etch them to gain sigil points.</p>
      </div>
    );
  }
  const equipped = c.equipment.stone?.uid === stone.uid;
  const etchLine = (i: number) => {
    const l = stone.lines[i];
    if (!l) return;
    const idx = l.nodes.indexOf(null);
    if (idx < 0) return;
    const before = stone.chance;
    const r = p.etchStone(stone.uid, i);
    if (r === null) {
      notify(a.wallet.silver < ETCH_COST ? 'Not enough silver to etch.' : 'This line cannot be etched.', '#e5484d');
      return;
    }
    const n = Date.now();
    setFlash({ line: i, idx, ok: r, n });
    setLog([{ n, ok: r, neg: l.negative, text: `${SIGILS[l.sigil]?.name ?? l.sigil} — ${r ? 'Success' : 'Failed'} at ${Math.round(before * 100)}%` }, ...log].slice(0, 5));
    commit();
  };
  const done = stone.lines.every((l) => l.nodes.every((x) => x !== null));
  return (
    <div class="bst">
      <div class="bst-left">
        {stones.length > 1 && (
          <div class="bst-pick" role="tablist" aria-label="Rune stones">
            {stones.map((s) => (
              <ItemCell key={s.uid} item={s} size={44} selected={s.uid === stone.uid} role="tab" aria-selected={s.uid === stone.uid} tipProps={{ where: c.equipment.stone?.uid === s.uid ? 'equipped' : 'bag' }} onClick={() => setUid(s.uid)} />
            ))}
          </div>
        )}
        <div class={`bst-art r-${stone.rarity}`} {...tip(() => <ItemTooltip item={stone} />)}>
          <img src={stoneIcon(stone)} alt="" />
        </div>
        <div class={`bst-name r-${stone.rarity}`}>{stone.name}</div>
        <div class="bst-where">{equipped ? 'Equipped' : 'In your bag'}</div>
        <div class="bst-chance">
          <span>Success chance</span>
          <b key={flash?.n} class="bst-chance-v" style={{ color: chanceColor(stone.chance) }}>
            {Math.round(stone.chance * 100)}%
          </b>
          <span class="bst-meter" aria-hidden="true">
            <i style={{ left: `${((stone.chance - 0.25) / 0.5) * 100}%` }} />
          </span>
          <small>Success lowers the chance by 10%, failure raises it (25–75%).</small>
        </div>
        <div class="bst-cost">
          <span>Cost per etch</span>
          <Money silver={ETCH_COST} />
        </div>
        <div class="bst-cost">
          <span>You have</span>
          <Money silver={a.wallet.silver} />
        </div>
        {!equipped && (
          <button
            type="button"
            class="btn"
            onClick={() => {
              if (equip(c, stone.uid)) {
                notify(`Equipped ${stone.name}`, `var(--r-${stone.rarity})`);
                commit({ stats: true });
              }
            }}
          >
            Equip this stone
          </button>
        )}
      </div>
      <div class="bst-lines">
        {stone.lines.map((l, i) => {
          const pts = stonePoints(l);
          const left = l.nodes.filter((x) => x === null).length;
          return (
            <div key={i} class={`bst-line ${l.negative ? 'is-neg' : ''}`}>
              <div class="bst-line-h">
                <img src={sigilIcon(l.sigil)} alt="" />
                <span class="bst-line-name">{SIGILS[l.sigil]?.name ?? l.sigil}</span>
                <span class="bst-line-pts">
                  +{pts} <small>pts</small>
                </span>
              </div>
              <div class="bst-nodes" role="img" aria-label={`${pts} successes, ${left} nodes left`}>
                {l.nodes.map((nd, k) => (
                  <i key={flash && flash.line === i && flash.idx === k ? `f${flash.n}` : k} class={`${nd === true ? 'ok' : nd === false ? 'no' : ''} ${flash && flash.line === i && flash.idx === k ? 'is-flash' : ''}`} />
                ))}
              </div>
              <div class="bst-line-f">
                <span>{left > 0 ? `${left} of ${STONE_NODES[stone.rarity]} nodes left` : 'Complete'}</span>
                <button type="button" class={`btn ${l.negative ? '' : 'btn--gold'}`} disabled={left === 0 || a.wallet.silver < ETCH_COST} onClick={() => etchLine(i)}>
                  Etch
                </button>
              </div>
            </div>
          );
        })}
        <div class="bst-log" aria-live="polite">
          {done && <div class="bst-log-done">Etching complete. The stone's sigils are set.</div>}
          {log.map((e) => (
            <div key={e.n} class={e.ok ? (e.neg ? 'is-bad' : 'is-ok') : 'is-fail'}>
              {e.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ====================================================================== gems

function GemsTab({ ctx }: { ctx: Ctx }) {
  const { c, p, a, cls } = ctx;
  const [sock, setSock] = useState(0);
  const [gemId, setGemId] = useState<string | null>(null);
  const [skillId, setSkillId] = useState<string | null>(null);
  const stacks = gemStacks(c);
  const skills = cls?.skills ?? [];
  const cur = c.gems[sock] ?? null;
  const chosenGem = stacks.find((s) => s.id === gemId) ?? null;
  const targetSkill = skillId ?? cur?.skillId ?? null;
  const doSocket = () => {
    if (!chosenGem || !targetSkill) return;
    if (p.socketGem(sock, chosenGem.id, targetSkill)) {
      notify(`${chosenGem.def.name} socketed into ${registry.skills.get(targetSkill)?.name ?? targetSkill}`, '#ffd070');
      setGemId(null);
      commit();
    }
  };
  const retarget = (sid: string) => {
    if (!cur) {
      setSkillId(sid);
      return;
    }
    const id = `gem_${cur.type}_${cur.level}`;
    if (p.unsocketGem(sock) && p.socketGem(sock, id, sid)) commit();
    else notify('Could not move the gem (is your bag full?).', '#e5484d');
  };
  const fusable = stacks.filter((s) => s.count >= 3 && s.level < GEM_MAX_LEVEL);
  const mods = Object.entries(p.build().skillMods);
  return (
    <div class="bgm">
      <div class="bgm-sockets" role="tablist" aria-label="Gem sockets">
        {c.gems.map((g, i) => {
          const sk = g ? registry.skills.get(g.skillId) : undefined;
          return (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={sock === i}
              class={`bgm-sock ${sock === i ? 'is-sel' : ''} ${g ? '' : 'is-empty'}`}
              onClick={() => {
                setSock(i);
                setSkillId(null);
                setGemId(null);
              }}
              {...(g ? tip(() => <ItemTooltip item={`gem_${g.type}_${g.level}`} count={1} where="other" />) : {})}
            >
              <span class="bgm-ring">
                {g ? <img src={gemIcon(g.type, g.level)} alt="" /> : <span class="bgm-plus">+</span>}
                {g && <span class="bgm-lv">{g.level}</span>}
              </span>
              <span class="bgm-target">
                {sk ? (
                  <>
                    <img src={skillIcon(sk, cls?.hue)} alt="" />
                    {sk.name}
                  </>
                ) : (
                  'Empty'
                )}
              </span>
            </button>
          );
        })}
      </div>
      <div class="bgm-body">
        <div class="bgm-edit">
          <div class="bgm-edit-h section-h">Socket {sock + 1}</div>
          {cur ? (
            <div class="bgm-cur">
              <img src={gemIcon(cur.type, cur.level)} alt="" />
              <div>
                <div class={`r-${ITEMS[`gem_${cur.type}_${cur.level}`]?.rarity ?? 'rare'} bgm-cur-name`}>{ITEMS[`gem_${cur.type}_${cur.level}`]?.name}</div>
                <div class="bgm-cur-eff">
                  {registry.skills.get(cur.skillId)?.name}: {cur.type === 'dmg' ? `damage +${cur.level * 3}%` : `cooldown −${cur.level * 2}%`}
                </div>
              </div>
              <button
                type="button"
                class="btn"
                onClick={() => {
                  if (p.unsocketGem(sock)) commit();
                  else notify('Your bag is full.', '#e5484d');
                }}
              >
                Remove
              </button>
            </div>
          ) : (
            <>
              <div class="bgm-step">1. Choose a gem</div>
              {stacks.length === 0 ? (
                <p class="muted bgm-none">You have no gems. They drop from story bosses, Colossus Hunts and the Rift Delve.</p>
              ) : (
                <div class="bgm-gems">
                  {stacks.map((s) => (
                    <ItemCell key={s.id} item={s.def} count={s.count} size={44} selected={gemId === s.id} tipProps={{ where: 'bag' }} onClick={() => setGemId(gemId === s.id ? null : s.id)} />
                  ))}
                </div>
              )}
            </>
          )}
          <div class="bgm-step">{cur ? 'Move to another skill' : '2. Choose a skill'}</div>
          <div class="bgm-skills">
            {skills.map((s) => (
              <button key={s.id} type="button" class={`bgm-skill ${targetSkill === s.id ? 'is-sel' : ''}`} aria-pressed={targetSkill === s.id} {...tip(() => <SkillTooltip skillId={s.id} />)} onClick={() => retarget(s.id)}>
                <img src={skillIcon(s, cls?.hue)} alt="" />
                <span>{s.name}</span>
              </button>
            ))}
          </div>
          {!cur && (
            <button type="button" class="btn btn--gold bgm-go" disabled={!chosenGem || !targetSkill} onClick={doSocket}>
              Socket gem
            </button>
          )}
        </div>
        <div class="bgm-fuse">
          <div class="section-h">Fusion</div>
          <p class="muted">Fuse three gems of the same kind and level into one of the next level.</p>
          {fusable.length === 0 ? (
            <p class="bgm-none muted">Nothing to fuse yet.</p>
          ) : (
            fusable.map((s) => (
              <div key={s.id} class="bgm-fuse-row">
                <ItemCell item={s.def} count={s.count} size={40} tipProps={{ where: 'bag' }} />
                <span class={`r-${s.def.rarity}`}>{s.def.name}</span>
                <button
                  type="button"
                  class="btn"
                  disabled={a.wallet.silver < fuseCost(s.level)}
                  onClick={() => {
                    if (p.fuseGems(s.type, s.level)) {
                      notify(`Fused into a Lv.${s.level + 1} gem`, '#ffd070');
                      commit();
                    } else notify('Not enough silver.', '#e5484d');
                  }}
                >
                  Fuse → Lv.{s.level + 1} <Money silver={fuseCost(s.level)} />
                </button>
              </div>
            ))
          )}
          <div class="section-h bgm-sum-h">Gem effects</div>
          <div class="bgm-sum">
            {mods.length === 0 ? (
              <p class="muted">No gems socketed.</p>
            ) : (
              mods.map(([sid, m]) => (
                <div key={sid} class="bgm-sum-row">
                  <span>{registry.skills.get(sid)?.name ?? sid}</span>
                  <b>{[m.dmg ? `+${fmtPct(m.dmg, 0)} dmg` : '', m.cd ? `−${fmtPct(m.cd, 0)} cd` : ''].filter(Boolean).join(' · ')}</b>
                </div>
              ))
            )}
          </div>
          <div class="bgm-wallet">
            <Money silver={a.wallet.silver} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ====================================================================== panel

export function SigilsPanel() {
  const ctx = useCtx();
  const arg = ui.panelArg.value ?? '';
  const [tab, setTab] = useState<Tab>(arg.includes('gem') ? 'gems' : arg.includes('stone') ? 'stone' : 'sigils');
  if (!ctx) return null;
  const tabs: { id: Tab; label: string }[] = [
    { id: 'sigils', label: 'Sigils' },
    { id: 'stone', label: 'Rune Stone' },
    { id: 'gems', label: 'Gems' },
  ];
  return (
    <Window title="Sigils" width={980} height={700} className="bwin bwin--sigils">
      <div class="bsgp" {...keyTrap}>
        <div class="bsgp-tabs" role="tablist" aria-label="Sigil sections">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" class="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
          <Money silver={ctx.a.wallet.silver} class="bsgp-wallet" />
        </div>
        <div class="bsgp-body">
          {tab === 'sigils' && <SigilsTab ctx={ctx} />}
          {tab === 'stone' && <StoneTab ctx={ctx} />}
          {tab === 'gems' && <GemsTab ctx={ctx} />}
        </div>
      </div>
    </Window>
  );
}
